import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";
import {apiGenericCreate, apiGenericUpdate, genericGetOne} from "../generic/crud";
import {ENTITY_ID_TREATMENT, ENTITY_ID_DEALER} from "../generic/Constants";
import {defaultTreatment} from "../generic/InitialStates";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {AGCText} from "../../generic-components/AGCText";
import {AGCSelect} from "../../generic-components/AGCSelect";

const VEHICLE_SEGMENTS = ['SMALL', 'MEDIUM', 'LARGE', 'PREMIUM'];
const JOB_TYPES = ['FREE_SERVICE', 'PAID_SERVICE', 'RUNNING_REPAIR', 'ACCIDENTAL_REPAIR'];

const SEGMENT_DISPLAY_NAMES = {
    'SMALL': 'Small',
    'MEDIUM': 'Medium',
    'LARGE': 'Large',
    'PREMIUM': 'Premium'
};

const JOB_TYPE_DISPLAY_NAMES = {
    'FREE_SERVICE': 'Free Service',
    'PAID_SERVICE': 'Paid Service',
    'RUNNING_REPAIR': 'Running Repair',
    'ACCIDENTAL_REPAIR': 'Accidental Repair'
};

export function TreatmentAddEditDialog(props) {
    const {show, handleClose, id, onSave} = props;
    const [forUpdate, setForUpdate] = useState(false);
    const [objectForSubmit, setObjectForSubmit] = useState(defaultTreatment);
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState("Treatment");
    const entityName = "Treatment";

    // Initialize pricing rows based on pricing type
    const initializePricingRows = (pricingType, existingPricing = []) => {
        const types = pricingType === 'SEGMENT_PRICING' ? VEHICLE_SEGMENTS : JOB_TYPES;

        return types.map(type => {
            const existing = existingPricing.find(p =>
                (pricingType === 'SEGMENT_PRICING' && p.segment === type) ||
                (pricingType === 'JOB_TYPE' && p.jobType === type)
            );

            return existing || {
                segment: pricingType === 'SEGMENT_PRICING' ? type : null,
                jobType: pricingType === 'JOB_TYPE' ? type : null,
                dealerPrice: 0,
                customerPrice: 0
            };
        });
    };

    const formik = useFormik({
        initialValues: objectForSubmit,
        enableReinitialize: true,
        validationSchema: Yup.object({
            name: Yup.string().required("Treatment Name is required"),
            hsnNumber: Yup.string().required("HSN/SAC Number is required"),
            dealerId: Yup.mixed().required("Dealer is required").test(
                'is-not-empty',
                'Dealer is required',
                value => {
                    if (!value) return false;
                    if (typeof value === 'object') return !!value.id;
                    return !!value;
                }
            ),
        }),
        onSubmit(values, {resetForm}) {
            setIsLoading(true);

            // Prepare data for submission without mutating formik values
            const submissionData = {
                ...values,
                dealerId: values.dealerId?.id || values.dealerId
            };

            const config = { suppressToast: true };

            if (!id) {
                apiGenericCreate(ENTITY_ID_TREATMENT, submissionData, config).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Created!", TOAST_SUCCESS);
                    resetForm();
                    if (onSave) onSave();
                }).catch((error) => {
                    setIsLoading(false);
                    handleValidationError(error);
                });
            } else {
                apiGenericUpdate(ENTITY_ID_TREATMENT, submissionData, id, config).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Updated!", TOAST_SUCCESS);
                    resetForm();
                    if (onSave) onSave();
                }).catch((error) => {
                    setIsLoading(false);
                    handleValidationError(error);
                });
            }
        }
    });

    const handleValidationError = (error) => {
        let errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || "An error occurred";

        if (typeof errorMessage === 'object') {
             try {
                 errorMessage = JSON.stringify(errorMessage);
             } catch (e) {
                 errorMessage = "An unknown error occurred";
             }
        }

        showToast("Error: " + errorMessage, TOAST_FAILURE);
    };

    useEffect(() => {
        if (id != null) {
            setForUpdate(true);
            setTitle("Edit " + entityName);
            setIsLoading(true);

            genericGetOne(ENTITY_ID_TREATMENT, id).then(
                ({data}) => {
                    // Convert dealer ID to select object
                    data.dealerId = (data.dealerId && data.dealerId !== "") ?
                        {id: data.dealerId, name: data.dealerName} : null;

                    // Initialize pricing if not present
                    if (!data.pricing || data.pricing.length === 0) {
                        data.pricing = initializePricingRows(data.pricingType || 'SEGMENT_PRICING');
                    }

                    setObjectForSubmit(data);
                    setTitle("Update " + entityName);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        } else {
            setForUpdate(false);
            const newTreatment = {
                ...defaultTreatment,
                pricing: initializePricingRows('SEGMENT_PRICING')
            };
            setObjectForSubmit(newTreatment);
            setTitle("New " + entityName);
        }
    }, [show, id]);

    const handlePricingTypeChange = (newPricingType) => {
        const updatedPricing = initializePricingRows(newPricingType, formik.values.pricing);
        formik.setFieldValue('pricingType', newPricingType);
        formik.setFieldValue('pricing', updatedPricing);
    };

    const handlePricingChange = (index, field, value) => {
        const updatedPricing = [...formik.values.pricing];
        updatedPricing[index] = {
            ...updatedPricing[index],
            [field]: value
        };
        formik.setFieldValue('pricing', updatedPricing);
    };

    const renderPricingTable = () => {
        const types = formik.values.pricingType === 'SEGMENT_PRICING' ? VEHICLE_SEGMENTS : JOB_TYPES;
        const displayNames = formik.values.pricingType === 'SEGMENT_PRICING' ?
            SEGMENT_DISPLAY_NAMES : JOB_TYPE_DISPLAY_NAMES;

        // Different light background colors for each pricing card
        const backgroundColors = [
            'bg-light-primary',   // Light blue
            'bg-light-success',   // Light green
            'bg-light-warning',   // Light orange/yellow
            'bg-light-info'       // Light cyan
        ];

        const badgeColors = [
            'badge-primary',
            'badge-success',
            'badge-warning',
            'badge-info'
        ];

        // Group pricing cards in pairs (2 per row)
        const pricingRows = [];
        for (let i = 0; i < formik.values.pricing.length; i += 2) {
            pricingRows.push(formik.values.pricing.slice(i, i + 2));
        }

        return (
            <>
                <h6 className="fw-bold mb-3 text-gray-800">
                    <i className="bi bi-currency-rupee fs-5 me-2"></i>
                    Pricing Details - For {formik.values.pricingType === 'SEGMENT_PRICING' ? 'Segment Pricing' : 'Job Type Pricing'}
                </h6>
                {pricingRows.map((row, rowIndex) => (
                    <div key={rowIndex} className="row mb-3">
                        {row.map((priceRow, colIndex) => {
                            const index = rowIndex * 2 + colIndex;
                            return (
                                <div key={index} className="col-lg-6">
                                    <div className={`card border border-gray-300 h-100 ${backgroundColors[index]}`}>
                                        <div className="card-body p-3">
                                            <div className="mb-2">
                                                <span className={`badge ${badgeColors[index]} fs-7 px-3 py-1`}>
                                                    {displayNames[types[index]]}
                                                </span>
                                            </div>
                                            <div className="mb-2">
                                                <Form.Label className="fw-bold fs-8 text-gray-700 mb-1">Dealer Price</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="0.00"
                                                    value={priceRow.dealerPrice ?? ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                            handlePricingChange(index, 'dealerPrice', val === '' ? '' : val);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const parsed = parseFloat(e.target.value);
                                                        handlePricingChange(index, 'dealerPrice', isNaN(parsed) ? 0 : parsed);
                                                    }}
                                                    className="form-control-sm"
                                                />
                                            </div>
                                            <div className="mb-2">
                                                <Form.Label className="fw-bold fs-8 text-gray-700 mb-1">Customer Price</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="0.00"
                                                    value={priceRow.customerPrice ?? ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                            handlePricingChange(index, 'customerPrice', val === '' ? '' : val);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const parsed = parseFloat(e.target.value);
                                                        handlePricingChange(index, 'customerPrice', isNaN(parsed) ? 0 : parsed);
                                                    }}
                                                    className="form-control-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </>
        );
    };

    const handleSaveClick = async () => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            showToast("Please fill all mandatory fields: Treatment Name, HSN/SAC Number, and Dealer", TOAST_FAILURE);
            formik.setTouched({
                name: true,
                hsnNumber: true,
                dealerId: true
            });
        } else {
            formik.handleSubmit();
        }
    };

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay" style={{maxHeight: 'calc(100vh - 200px)', overflowY: 'auto'}}>
                {!isLoading && (
                    <Form className="form form-label-right" onSubmit={(e) => e.preventDefault()}>
                        {/* Name and VAS */}
                        <div className="form-group row">
                            <Form.Group className="col-lg-8">
                                <AGCText
                                    formik={formik}
                                    name={"Treatment Name"}
                                    placeHolder={'Enter Treatment Name'}
                                    id={"name"}
                                    toolTip={"Please enter the Treatment Name"}
                                    value={formik.values.name}
                                />
                            </Form.Group>
                            <Form.Group className="col-lg-4 d-flex align-items-center pt-8">
                                <Form.Check
                                    type="checkbox"
                                    id="isVAS"
                                    label="is VAS"
                                    checked={formik.values.isVAS}
                                    onChange={(e) => formik.setFieldValue('isVAS', e.target.checked)}
                                    className="fw-bold"
                                />
                            </Form.Group>
                        </div>

                        {/* Description */}
                        <div className="form-group row">
                            <Form.Group className="col-lg-12">
                                <Form.Label className="fw-bold">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Enter Treatment Description"
                                    value={formik.values.description}
                                    onChange={(e) => formik.setFieldValue('description', e.target.value)}
                                />
                            </Form.Group>
                        </div>

                        {/* HSN Number and Dealer */}
                        <div className="form-group row">
                            <Form.Group className="col-lg-6">
                                <AGCText
                                    formik={formik}
                                    name={"HSN/SAC Number"}
                                    placeHolder={'Enter HSN/SAC Number'}
                                    id={"hsnNumber"}
                                    toolTip={"Please enter the HSN/SAC Number"}
                                    value={formik.values.hsnNumber}
                                />
                            </Form.Group>
                            <Form.Group className="col-lg-6">
                                <AGCSelect
                                    formik={formik}
                                    isMulti={false}
                                    id={'dealerId'}
                                    name={"Select Dealer"}
                                    entityId={ENTITY_ID_DEALER}
                                    value={formik.values.dealerId}
                                    toolTip={"Please Select a Dealer"}
                                />
                            </Form.Group>
                        </div>

                        {/* Category and Pricing Type in same row */}
                        <div className="form-group row">
                            <Form.Group className="col-lg-6">
                                <Form.Label className="fw-bold">Category</Form.Label>
                                <div className="d-flex flex-column gap-2">
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${formik.values.category === 'HVT' ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => formik.setFieldValue('category', 'HVT')}
                                    >
                                        HVT (High Value)
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${formik.values.category === 'LVT' ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => formik.setFieldValue('category', 'LVT')}
                                    >
                                        LVT (Low Value)
                                    </button>
                                </div>
                            </Form.Group>

                            <Form.Group className="col-lg-6">
                                <Form.Label className="fw-bold">Pricing Type</Form.Label>
                                <div className="d-flex flex-column gap-2">
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${formik.values.pricingType === 'SEGMENT_PRICING' ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => handlePricingTypeChange('SEGMENT_PRICING')}
                                    >
                                        Segment Pricing
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${formik.values.pricingType === 'JOB_TYPE' ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => handlePricingTypeChange('JOB_TYPE')}
                                    >
                                        Job Type Pricing
                                    </button>
                                </div>
                            </Form.Group>
                        </div>

                        <div className="separator separator-dashed my-4"></div>

                        {/* Pricing Table */}
                        {renderPricingTable()}

                        <br/>
                        <Modal.Footer>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-elevate"
                                    onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveClick}
                                    className="btn btn-primary btn-elevate">
                                    Save
                                </button>
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
                {isLoading && <span>Please Wait, Fetching Information from Server</span>}
            </Modal.Body>
        </Modal>
    );
}
